import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { 
  ArrowRight, 
  CheckCircle2, 
  Target, 
  FileText, 
  Briefcase,
  LogOut,
  Loader2,
  ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user, logout, loading: authLoading } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/app/login');
      return;
    }
    if (user) {
      fetchDashboard();
    }
  }, [user, authLoading, navigate]);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/app/student/dashboard`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/app/login');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const nextAction = dashboardData?.next_action;
  const planProgress = dashboardData?.plan_progress;
  const targetRole = dashboardData?.target_role;
  const assessmentSummary = dashboardData?.assessment_summary;

  // Dimension display names
  const dimensionNames = {
    career_direction: 'Career Direction',
    profile_strength: 'Profile Strength',
    interview_readiness: 'Interview Readiness',
    job_search: 'Job Search',
  };

  // Band colors
  const getBandColor = (band) => {
    switch (band) {
      case 'Strong': return 'bg-emerald-100 text-emerald-700';
      case 'Developing Well': return 'bg-blue-100 text-blue-700';
      case 'Needs Attention': return 'bg-amber-100 text-amber-700';
      case 'Priority Gap': return 'bg-orange-100 text-orange-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="bg-white border border-slate-200 px-2 py-1 rounded-lg">
              <img 
                src="/logo-elevate-careers.webp" 
                alt="Elevate Careers" 
                className="h-6 w-auto"
              />
            </div>
            <span className="text-sm font-medium text-teal-600 hidden sm:inline">CareerOS</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:inline">
              {dashboardData?.student?.full_name || user.full_name}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-500 hover:text-slate-700"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Welcome Message */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Hello, {dashboardData?.student?.full_name?.split(' ')[0] || user.full_name?.split(' ')[0]}
          </h1>
          <p className="text-slate-600 mt-1">
            {targetRole 
              ? `Working towards: ${targetRole.name}`
              : 'Let\'s get you started on your career journey'}
          </p>
        </div>

        {/* Next Best Action Card */}
        {nextAction && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${
                  nextAction.type === 'assessment' ? 'bg-teal-100' :
                  nextAction.type === 'role_select' ? 'bg-blue-100' :
                  nextAction.type === 'task' ? 'bg-amber-100' :
                  'bg-emerald-100'
                }`}>
                  {nextAction.type === 'assessment' ? (
                    <FileText className="w-6 h-6 text-teal-600" />
                  ) : nextAction.type === 'role_select' ? (
                    <Target className="w-6 h-6 text-blue-600" />
                  ) : nextAction.type === 'task' ? (
                    <CheckCircle2 className="w-6 h-6 text-amber-600" />
                  ) : (
                    <Briefcase className="w-6 h-6 text-emerald-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-teal-600 uppercase tracking-wider mb-1">
                    Next Step
                  </p>
                  <h2 className="text-lg font-semibold text-slate-900 mb-1">
                    {nextAction.title}
                  </h2>
                  <p className="text-slate-600 text-sm">
                    {nextAction.description}
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <Link to={nextAction.link}>
                  <Button 
                    className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-11"
                    data-testid="next-action-cta"
                  >
                    {nextAction.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Plan Progress */}
        {planProgress && planProgress.total_tasks > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Your Progress</h3>
              <Link 
                to="/app/action-plan"
                className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
              >
                View Plan <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="mb-3">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-600">
                  {planProgress.completed_tasks} of {planProgress.total_tasks} tasks completed
                </span>
                <span className="font-medium text-teal-600">
                  {planProgress.completion_percentage}%
                </span>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-teal-600 rounded-full transition-all duration-500"
                  style={{ width: `${planProgress.completion_percentage}%` }}
                />
              </div>
            </div>
            {planProgress.in_progress_tasks > 0 && (
              <p className="text-sm text-slate-500">
                {planProgress.in_progress_tasks} task{planProgress.in_progress_tasks > 1 ? 's' : ''} in progress
              </p>
            )}
          </div>
        )}

        {/* Assessment Summary */}
        {assessmentSummary && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 sm:p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Your Readiness Profile</h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(assessmentSummary.bands || {}).map(([dimension, band]) => (
                <div key={dimension} className="p-3 bg-slate-50 rounded-xl">
                  <p className="text-xs text-slate-500 mb-1">
                    {dimensionNames[dimension] || dimension}
                  </p>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getBandColor(band)}`}>
                    {band}
                  </span>
                </div>
              ))}
            </div>
            {assessmentSummary.priorityArea && (
              <p className="mt-4 text-sm text-slate-600">
                <span className="font-medium">Focus area:</span>{' '}
                {dimensionNames[assessmentSummary.priorityArea] || assessmentSummary.priorityArea}
              </p>
            )}
          </div>
        )}

        {/* Quick Links */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Link 
            to="/app/profile"
            className="p-4 bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-sm transition-all"
          >
            <FileText className="w-5 h-5 text-slate-400 mb-2" />
            <p className="text-sm font-medium text-slate-900">Profile</p>
          </Link>
          <Link 
            to="/app/assessment"
            className="p-4 bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-sm transition-all"
          >
            <Target className="w-5 h-5 text-slate-400 mb-2" />
            <p className="text-sm font-medium text-slate-900">Assessment</p>
          </Link>
          <Link 
            to="/app/action-plan"
            className="p-4 bg-white rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-sm transition-all"
          >
            <CheckCircle2 className="w-5 h-5 text-slate-400 mb-2" />
            <p className="text-sm font-medium text-slate-900">Action Plan</p>
          </Link>
        </div>
      </main>
    </div>
  );
}
